import type { RequestArchiveReason } from "@nlc/shared-types";

import {
  ARCHIVE_MODAL_COPY,
  ARCHIVE_REASON_LABELS,
  archiveReasons,
} from "@nlc/shared-types";
import { useState } from "react";
import { ToggleButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import {
  CHIP_BASE,
  CHIP_NEUTRAL,
  CHIP_SELECTED,
} from "@/components/chip-styles";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogTitle,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { useArchiveRequest } from "@/features/request/edit/hooks/useArchiveRequest";

interface ArchiveNoDeliverableModalProps {
  isOpen: boolean;
  onHasDeliverable?: () => void;
  onOpenChange: (open: boolean) => void;
}

export function ArchiveNoDeliverableModal({
  isOpen,
  onHasDeliverable,
  onOpenChange,
}: ArchiveNoDeliverableModalProps) {
  const { archiveRequest, isPending } = useArchiveRequest();
  const [reason, setReason] = useState<RequestArchiveReason | undefined>();
  const [comment, setComment] = useState("");

  const close = (open: boolean) => {
    if (!open) {
      setReason(undefined);
      setComment("");
    }
    onOpenChange(open);
  };

  const handleArchive = () => {
    archiveRequest(
      { comment: comment || undefined, reason },
      () => close(false),
      () => {
        close(false);
        onHasDeliverable?.();
      },
    );
  };

  return (
    <Modal
      classNames={{ modal: "w-[560px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={close}
    >
      <Dialog>
        <DialogBody>
          <div className="flex flex-col gap-lg">
            <div>
              <DialogTitle className="text-xl font-semibold text-text-primary">
                {ARCHIVE_MODAL_COPY.title}
              </DialogTitle>
              <DialogCloseButton />
              <p className="mt-xs text-sm text-text-tertiary">
                {ARCHIVE_MODAL_COPY.body}
              </p>
            </div>

            <div className="flex flex-col gap-sm">
              <p className="text-md font-semibold text-text-primary">
                {ARCHIVE_MODAL_COPY.reasonLabel}
              </p>
              <div className="flex flex-wrap gap-sm">
                {archiveReasons.map((option) => {
                  const isSelected = reason === option;

                  return (
                    <ToggleButton
                      className={twMerge(
                        CHIP_BASE,
                        "px-lg py-xs",
                        isSelected ? CHIP_SELECTED : CHIP_NEUTRAL,
                      )}
                      isDisabled={isPending}
                      isSelected={isSelected}
                      key={option}
                      onChange={() =>
                        setReason(isSelected ? undefined : option)
                      }
                    >
                      {ARCHIVE_REASON_LABELS[option]}
                    </ToggleButton>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <textarea
                className="w-full resize-none rounded-lg border border-border-primary bg-bg-primary p-md pb-xl text-sm text-text-primary outline-hidden focus-visible:border-border-brand-solid disabled:bg-bg-secondary"
                disabled={isPending}
                maxLength={500}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Anything else you'd like to share? (optional)"
                rows={3}
                value={comment}
              />
              <span className="pointer-events-none absolute bottom-md right-md text-xs text-text-tertiary">
                {comment.length}/500
              </span>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            className="w-full"
            isDisabled={isPending}
            onPress={handleArchive}
            size="lg"
            variant="primary"
          >
            {isPending ? "Archiving..." : ARCHIVE_MODAL_COPY.confirmLabel}
          </Button>
          <Button
            color="gray"
            isDisabled={isPending}
            onPress={() => close(false)}
            size="lg"
            variant="tertiary"
          >
            {ARCHIVE_MODAL_COPY.cancelLabel}
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
